import { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setAppError } from "../../../../../store/userSlice";
import SmartDownloadPagination from "../../../../../components/SmartDataTable/SmartDownloadPagination";
import AbscondingRunPopup from "./AbscondingRunPopup/AbscondingRunPopup";
import { GoEye } from "react-icons/go";
import { checkAccessFn } from "../../../../../utils/checkAccess";
import {
  ABSCONDING_RUNWAY_COLUMNS,
  ABSCONDING_RUNWAY_CONFIG,
  ABSCONDING_RUNWAY_SEARCHABLE_FIELDS,
  formatDate,
  renderVisaProvider,
  renderAbscondingStatus
} from './AbscondingRunWayConstants';

const AbscondingRunWayList = ({
  setTrigger,
  trigger,
}) => {
  const dispatch = useDispatch();
  const dashboardData = useSelector((state) => state.dashboardData);
  const checkAccess = (module, name) => checkAccessFn(dashboardData, module, name);
  
  // Get token from localStorage
  const token = useMemo(() => localStorage.getItem("nodeToken"), []);

  // Data tracking state for UI optimization
  const [dataState, setDataState] = useState(() => ({
    hasData: false,
    isLoading: true,
    totalRecords: 0
  }));

  // Popup state management
  const [popupState, setPopupState] = useState(() => ({
    absconding: false,
    propData: null
  }));

  // Data load handler to track data availability
  const handleDataLoad = useCallback((data, pagination) => {
    const dataExists = data && data.length > 0;
    setDataState({
      hasData: dataExists,
      isLoading: pagination.isLoading,
      totalRecords: pagination.totalRecords || 0
    });
  }, []);

  // Handle eye icon click to open popup
  const handleViewClick = useCallback((item) => {
    setPopupState({
      absconding: true,
      propData: item
    });
  }, []);

  // Close popup handler
  const handleClosePopup = useCallback(() => {
    setPopupState({
      absconding: false,
      propData: null
    });
  }, []);

  // API callback for refresh after popup actions
  const apiCall = useCallback(() => {
    // Trigger refresh by updating trigger state in parent
    setTrigger(prev => !prev);
  }, [setTrigger]);
  // Custom cell renderer for special cases
  const renderCustomCell = useCallback((item, column, rowIndex, currentPage, pageSize) => {
    switch (column.key) {
      case 'serial':
        return (currentPage - 1) * pageSize + rowIndex + 1;
      
      case 'visa_provider':
        return renderVisaProvider(item);
      
      case 'total_salary':
        return item?.total_salary?.toString() || "-";
      
      case 'aging':
        return item?.aging?.toString() || "-";
      
      case 'absconding_status':
        return renderAbscondingStatus(item);
      
      case 'action':
        return checkAccess("DEMOBILIZATION", "run-away-edit") ? (
          <div
            className="ml-2 cursor-pointer"
            onClick={() => handleViewClick(item)}
          >
            <GoEye fontSize={22} className="ml-2" />
          </div>
        ) : null;
      
      default:
        return null;
    }
  }, [handleViewClick, dashboardData]);

  // Download handlers
  const handleDownloadComplete = useCallback((result) => {
    dispatch(setAppError({
      msg: `Excel Downloaded (${result.recordCount} records)`,
      color: "success",
    }));
  }, [dispatch]);

  const handleDownloadError = useCallback((error) => {
    dispatch(setAppError({
      msg: error.message || "Download failed",
      color: "error"
    }));
  }, [dispatch]);

  return (
    <div className="p-2">
      <SmartDownloadPagination
        // API Configuration
        apiEndpoint={ABSCONDING_RUNWAY_CONFIG.API_ENDPOINT}
        downloadEndpoint={ABSCONDING_RUNWAY_CONFIG.DOWNLOAD_ENDPOINT}
        token={token}
        
        // Table Configuration
        columns={ABSCONDING_RUNWAY_COLUMNS}
        searchableFields={ABSCONDING_RUNWAY_SEARCHABLE_FIELDS}
        enableHighlighting={true}
        
        // Pagination Configuration
        defaultPageSize={ABSCONDING_RUNWAY_CONFIG.DEFAULT_PAGE_SIZE}
        pageSizeOptions={ABSCONDING_RUNWAY_CONFIG.PAGE_SIZE_OPTIONS}
        
        // Download Configuration
        downloadFilename={ABSCONDING_RUNWAY_CONFIG.DOWNLOAD_FILENAME}
        enableDownload={true}
        
        // Search Configuration
        enableSearch={true}
        searchPlaceholder={ABSCONDING_RUNWAY_CONFIG.SEARCH_PLACEHOLDER}
        searchDebounceMs={ABSCONDING_RUNWAY_CONFIG.DEBOUNCE_MS}
        
        // Trigger for cache invalidation
        trigger={trigger}
        
        // Customization
        renderCustomCell={renderCustomCell}
        
        // Event Handlers
        onDataLoad={handleDataLoad}
        onDownloadComplete={handleDownloadComplete}
        onDownloadError={handleDownloadError}
        
        // Additional Props
        noDataMessage={ABSCONDING_RUNWAY_CONFIG.NO_DATA_MESSAGE}
        className="absconding-runway-table"
      />

      {/* Popup rendering */}
      {popupState.absconding && (
        <AbscondingRunPopup
          PopUp={popupState.absconding}
          setPopUp={handleClosePopup}
          setTrigger={setTrigger}
          trigger={trigger}
          propData={popupState.propData}
        />
      )}
    </div>
  );
};

AbscondingRunWayList.displayName = 'AbscondingRunWayList';

export default AbscondingRunWayList;
